/**
 * Donut-chart geometry.
 *
 * `donutArcs` turns a list of values into a start/end angle and an SVG path
 * per slice; `arcPath` turns one start/end angle pair into the `d` string
 * for an annulus segment (an arc at the outer radius, cut out by an arc at
 * the inner radius). This module knows nothing about SVG *elements*,
 * colours, or Svelte — a component reads the numbers it needs off `ArcPath`
 * and drops `d` straight into a `<path>`. No chart library backs this; see
 * the task brief for why (bundle size, theming).
 *
 * Per src/lib/calc/numbers.ts: pure, never throws, never emits NaN/Infinity.
 * An all-zero or empty `values` array degenerates to "draw nothing" (`[]`)
 * rather than a divide-by-zero NaN, because NaN in a `d` attribute renders
 * nothing with no error — the exact silent failure that rule exists to
 * prevent.
 */

export interface Slice {
  value: number;
}

export interface ArcPath {
  d: string;
  start: number;
  end: number;
  midAngle: number;
}

const TWO_PI = Math.PI * 2;
const TWELVE_OCLOCK = -Math.PI / 2;

/**
 * How far short of a full turn the last slice is capped when it would
 * otherwise be exactly `TWO_PI`. A sweep of exactly `TWO_PI` puts the arc's
 * start and end point at the same (x, y) — and per the SVG spec, an arc
 * whose endpoints coincide is rendered as if it were omitted entirely, so a
 * single 100% slice would draw nothing (or, once the two `A` commands and
 * the `L` between them are accounted for, a hairline radial rather than a
 * filled ring). 0.001 rad (~0.057°) is small enough that no eye will ever
 * see the gap, but large enough that the two endpoints still round to
 * different coordinates at 3 decimals (see `r3` below) even at the small
 * end of the radii this module is actually called with.
 */
const FULL_SWEEP_GAP = 0.001;

/**
 * One arc per positive value, each spanning its share of the circle,
 * starting at twelve o'clock and sweeping clockwise.
 *
 * A value that is zero, negative, or non-finite is skipped entirely rather
 * than emitted as a zero-width arc: a zero-width path is invisible but still
 * present in the DOM (and the accessibility tree), which is worse than
 * simply absent. When nothing is positive there is nothing to draw, so the
 * result is `[]`, not a single arc covering the whole circle.
 *
 * A slice's sweep is capped just under `TWO_PI` (see `FULL_SWEEP_GAP`) so a
 * single value that is the whole total — the ordinary case of a month with
 * one spending category, or a small operation with only one — still draws a
 * complete ring instead of degenerating to a sliver. Sweeps only reach
 * `TWO_PI` when a slice is the sole positive value, since every other
 * combination divides the circle among two or more slices; the cap is a
 * no-op for any chart with more than one slice.
 */
export function donutArcs(values: readonly number[], radius: number, inner: number): ArcPath[] {
  const sum = values.reduce((total, v) => total + (Number.isFinite(v) && v > 0 ? v : 0), 0);
  if (sum <= 0) return [];

  const arcs: ArcPath[] = [];
  let angle = TWELVE_OCLOCK;
  for (const value of values) {
    if (!Number.isFinite(value) || value <= 0) continue;
    const sweep = Math.min((value / sum) * TWO_PI, TWO_PI - FULL_SWEEP_GAP);
    const start = angle;
    const end = angle + sweep;
    arcs.push({ d: arcPath(start, end, radius, inner), start, end, midAngle: start + sweep / 2 });
    angle = end;
  }
  return arcs;
}

/**
 * The `d` attribute for one annulus segment: out along the outer radius,
 * across the outer arc, in to the inner radius, back across the inner arc,
 * closed.
 *
 * The large-arc flag is 1 when the sweep exceeds half a turn (`Math.PI`).
 * Getting this backwards produces a chart that looks right for small slices
 * and inverts for large ones — the classic SVG arc bug, and one that only
 * shows up once a slice is over 50%.
 *
 * Every coordinate is rounded to 3 decimals so the same slice always emits
 * the same string; unrounded floating-point noise in the low digits would
 * make identical charts diff or fail a future snapshot test for no reason.
 */
export function arcPath(startAngle: number, endAngle: number, radius: number, inner: number): string {
  const start = safe(startAngle);
  const end = safe(endAngle);
  const r = safe(radius);
  const ir = safe(inner);
  const largeArc = Math.abs(end - start) > Math.PI ? 1 : 0;

  const outerStart = point(start, r);
  const outerEnd = point(end, r);
  const innerEnd = point(end, ir);
  const innerStart = point(start, ir);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${r3(r)} ${r3(r)} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${r3(ir)} ${r3(ir)} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z'
  ].join(' ');
}

function point(angle: number, radius: number): { x: number; y: number } {
  return { x: r3(Math.cos(angle) * radius), y: r3(Math.sin(angle) * radius) };
}

function r3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function safe(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
